import { Order } from "@dining/api-client";
import { AppProps, StateSlice } from "./app";

export type OrdersHistoryProps = {
  ordersHistory: {
    values?: Order[];
    loading?: boolean;
    error?: Error;
    selected?: Order;
    reorderingEnabled?: boolean;
  };
};

export type OrdersHistoryState = OrdersHistoryProps & {
  ordersHistory: {
    fetch: () => Promise<void>;
    setLoading: (loading: boolean) => void;
    setValues: (values?: Order[]) => void;
    setSelected: (selected?: Order) => void;
    setError: (error?: Error) => void;
    setReorderingEnabled: (reorderingEnabled: boolean) => void;
    reOrder: (orderId: string, reOrderConsent: boolean) => Promise<Order>;
  };
};

export const defaultOrdersHistoryProps: OrdersHistoryProps = {
  ordersHistory: {
    loading: false,
    reorderingEnabled: false,
  },
};

export const createOrdersHistoryState: (
  props?: Partial<AppProps>,
) => StateSlice<OrdersHistoryState> = (props) => (set, get) => ({
  ordersHistory: {
    ...defaultOrdersHistoryProps.ordersHistory,
    ...props?.ordersHistory,
    setLoading: (loading: boolean) =>
      set(
        (state) => ({
          ...state,
          ordersHistory: {
            ...state.ordersHistory,
            loading,
          },
        }),
        false,
        {
          type: "ordersHistory.setLoading",
          loading,
        },
      ),
    setValues: (values?: Order[]) =>
      set(
        (state) => ({
          ...state,
          ordersHistory: {
            ...state.ordersHistory,
            values,
          },
        }),
        false,
        {
          type: "ordersHistory.setValues",
          values,
        },
      ),
    setSelected: (selected?: Order) =>
      set(
        (state) => ({
          ...state,
          ordersHistory: {
            ...state.ordersHistory,
            selected,
          },
        }),
        false,
        {
          type: "ordersHistory.setSelected",
          selected,
        },
      ),
    setReorderingEnabled: (reorderingEnabled: boolean) =>
      set(
        (state) => ({
          ...state,
          ordersHistory: {
            ...state.ordersHistory,
            reorderingEnabled,
          },
        }),
        false,
        {
          type: "ordersHistory.setReorderingEnabled",
          reorderingEnabled,
        },
      ),
    setError: (error?: Error) =>
      set(
        (state) => ({
          ...state,
          ordersHistory: {
            ...state.ordersHistory,
            error,
          },
        }),
        false,
        {
          type: "ordersHistory.setError",
          error,
        },
      ),
    fetch: async () => {
      const {
        client,
        ordersHistory: { setValues, setReorderingEnabled, setError },
      } = get();
      try {
        if (client) {
          const hasAccessToReordering = await client.hasAccessToReordering();

          setReorderingEnabled(!!hasAccessToReordering);
          const response = await client.getOrders(10, !!hasAccessToReordering);
          setValues(response);

          console.info("Reordering enabled: ", !!hasAccessToReordering);
        } else {
          setError(new Error("client is not defined"));
        }
      } catch (error) {
        setError(error as Error);
      }
    },
    reOrder: async (orderId: string, reOrderConsent?: boolean): Promise<Order> => {
      const {
        client,
        ordersHistory: { setValues, setError },
      } = get();
      if (!client) {
        const error = new Error("client is not defined");
        setError(error);
        throw error;
      }
      try {
        const response = await client.reOrder(orderId, reOrderConsent);
        console.log("Order ID : ", orderId);
        setValues([response]);
        console.info("Reorder successful: ", response);
        return response;
      } catch (error) {
        setError(error as Error);
        throw error;
      }
    },
  },
});
